<template>
  <div style="width: 100%">
    <template v-if="!dashboards.length">
      <div class="no-data-msg">
        <p>Oops...</p>
        <p>{{ errorMessage }}</p>
      </div>
    </template>
    <template v-else>
      <div
        class="dashboard-filter"
        :style="{ top: `${contentTop}px` }"
      >
        <DashboardFilter
          :search-text="filterSettings.searchText"
          :selected-tags="filterSettings.selectedTags"
          :selected-owners="filterSettings.selectedOwners"
          :selected-type="filterSettings.selectedType"
          :only-favorites="filterSettings.onlyFavorites"
          :tag-options="tagOptions"
          :owner-options="ownerOptions"
          :type-options="typeOptions"
          :view-type="viewType"
          @on-search-text-change="onSearchTextChange"
          @on-selected-tags-change="onSelectedTagsChange"
          @on-selected-owners-change="onSelectedOwnersChange"
          @on-selected-type-change="onSelectedTypeChange"
          @on-only-favorites-change="onOnlyFavoritesChange"
          @on-clear-filters="clearFilters"
          @on-view-type-change="changeView"
        />
      </div>

      <HintBox
        :show-hint-box="showHintBox"
        :hints="hints"
        show-icon-big
        color-icon
        @closeHint="showHintBox = false"
        @updateHintPreference="updateHintPreference"
      />

      <div
        class="results"
        :class="viewType"
      >
        <div
          v-if="!filteredDashboards.length"
          class="no-data-msg"
        >
          No Dashboard matches the filters
        </div>
        <div v-else>
          <Row
            type="flex"
            class="dashboards-row"
          >
            <template v-if="viewType === 'grid'">
              <i-col
                v-for="(dashboard, index) in dashboardsPerPage"
                :key="index"
                class="card-block"
              >
                <Card class="dashboard-link-card">
                  <div class="dashboard-card-logo">
                    <div class="dashboard-logo">
                      <Icon
                        v-if="iconType(dashboard.icon) === 'iview'"
                        :type="dashboard.icon"
                        class="logo-icon"
                      />
                      <i
                        v-else
                        :class="`${dashboard.icon} logo-icon fa`"
                      />
                      <div
                        v-if="loading"
                        class="loading-veil"
                      />
                    </div>
                  </div>
                  <div
                    class="dashboard-card-body"
                    @click="isMobile
                      ? openDashboard(dashboard)
                      : previewDashboard(dashboard)"
                  >
                    <h4 class="dashboard-card-body-title">
                      {{ dashboard.title }}
                    </h4>
                    <p class="description">
                      {{ dashboard.description }}
                    </p>
                    <div class="dashboard-card-footer">
                      <Tag
                        v-for="tag in dashboard.tags"
                        :key="tag"
                        :title="tag"
                        class="tag"
                        @click.native="toggleTag(tag, true)"
                      >
                        #{{ capitalizeTagName(tag) }}
                      </Tag>
                    </div>
                  </div>
                  <Spin
                    v-if="loading"
                    fix
                    size="large"
                  />
                </Card>
                <div class="actions-section">
                  <div class="footer-section left">
                    <div
                      v-if="isMobile && Number.isInteger(dashboard.id)"
                      class="footer-icon"
                    >
                      <i
                        :class="{ 'fas fa-thumbs-up': dashboard.isLike, 'far fa-thumbs-up': !dashboard.isLike }"
                        @click="togglePreference('like', dashboard.id, { isActive: !dashboard.isLike, id: dashboard.likeId })"
                      />
                    </div>
                    <Tooltip
                      v-if="!isMobile && Number.isInteger(dashboard.id)"
                      content="Like Dashboard"
                      placement="bottom-start"
                      class="footer-icon"
                    >
                      <i
                        :class="{ 'fas fa-thumbs-up': dashboard.isLike, 'far fa-thumbs-up': !dashboard.isLike }"
                        @click="togglePreference('like', dashboard.id, { isActive: !dashboard.isLike, id: dashboard.likeId })"
                      />
                    </Tooltip>
                    <p
                      v-show="dashboard.total_likes"
                      class="footer-like-text"
                    >
                      {{ dashboard.total_likes }}
                      <span v-show="dashboard.total_likes === 1">like</span>
                      <span v-show="dashboard.total_likes > 1">likes</span>
                    </p>
                  </div>
                  <div class="footer-section right">
                    <template v-if="isMobile">
                      <div class="footer-icon">
                        <i
                          class="fas fa-share-alt"
                          @click="shareLink(dashboard.id)"
                        />
                      </div>
                      <div class="footer-icon">
                        <i
                          class="far fa-envelope"
                          @click="$feedback(dashboard.owners, dashboard.title, 'dashboard', `${$route.path}/${dashboard.id}`)"
                        />
                      </div>
                      <div
                        v-if="Number.isInteger(dashboard.id)"
                        class="footer-icon"
                      >
                        <i
                          :class="{ 'fas fa-heart': dashboard.isFavorite, 'far fa-heart': !dashboard.isFavorite }"
                          @click="togglePreference('favorite', dashboard.id, { isActive: !dashboard.isFavorite, id: dashboard.favoriteId })"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <Tooltip
                        content="Share Dashboard"
                        placement="bottom-end"
                        class="footer-icon"
                      >
                        <i
                          class="fas fa-share-alt"
                          @click="shareLink(dashboard.id)"
                        />
                      </Tooltip>
                      <Tooltip
                        :content="`Email ${dashboard.owners}`"
                        max-width="400"
                        placement="bottom-end"
                        class="footer-icon"
                      >
                        <i
                          class="far fa-envelope"
                          @click="$feedback(dashboard.owners, dashboard.title, 'dashboard', `${$route.path}/${dashboard.id}`)"
                        />
                      </Tooltip>
                      <Tooltip
                        v-if="Number.isInteger(dashboard.id)"
                        content="Favorite Dashboard"
                        placement="bottom-end"
                        class="footer-icon"
                      >
                        <i
                          :class="{ 'fas fa-heart': dashboard.isFavorite, 'far fa-heart': !dashboard.isFavorite }"
                          @click="togglePreference('favorite', dashboard.id, { isActive: !dashboard.isFavorite, id: dashboard.favoriteId })"
                        />
                      </Tooltip>
                    </template>
                  </div>
                  <div
                    v-if="loading"
                    class="loading-veil"
                  />
                </div>
              </i-col>
            </template>
            <template v-else>
              <div
                v-for="(dashboard, index) in dashboardsPerPage"
                :key="index"
                class="list-block"
              >
                <div class="list-row">
                  <div
                    class="title-section"
                    @click="isMobile ? openDashboard(dashboard) : previewDashboard(dashboard)"
                  >
                    <p class="title">
                      {{ dashboard.title }}
                    </p>
                    <div class="tags-section">
                      <Tag
                        v-for="tag in dashboard.tags"
                        :key="tag"
                        :title="tag"
                        class="tag"
                        @click.native="toggleTag(tag, true)"
                      >
                        #{{ tag }}
                      </Tag>
                    </div>
                  </div>
                  <div class="icons-section">
                    <div class="left-section">
                      <div
                        v-if="isMobile && Number.isInteger(dashboard.id)"
                        class="icon"
                      >
                        <i
                          :class="{ 'fas fa-thumbs-up': dashboard.isLike, 'far fa-thumbs-up': !dashboard.isLike }"
                          @click="togglePreference('like', dashboard.id, { isActive: !dashboard.isLike, id: dashboard.likeId })"
                        />
                      </div>
                      <Tooltip
                        v-if="!isMobile && Number.isInteger(dashboard.id)"
                        content="Like Dashboard"
                        placement="top-start"
                        class="icon"
                      >
                        <i
                          :class="{ 'fas fa-thumbs-up': dashboard.isLike, 'far fa-thumbs-up': !dashboard.isLike }"
                          @click="togglePreference('like', dashboard.id, { isActive: !dashboard.isLike, id: dashboard.likeId })"
                        />
                      </Tooltip>
                      <p
                        v-show="dashboard.total_likes"
                        class="like-text"
                      >
                        {{ dashboard.total_likes }}
                        <span v-show="dashboard.total_likes === 1">like</span>
                        <span v-show="dashboard.total_likes > 1">likes</span>
                      </p>
                    </div>
                    <div class="right-section">
                      <template v-if="isMobile">
                        <div class="icon">
                          <i
                            class="fas fa-share-alt"
                            @click="shareLink(dashboard.id)"
                          />
                        </div>
                        <div class="icon">
                          <i
                            class="far fa-envelope"
                            @click="$feedback(dashboard.owners, dashboard.title, 'dashboard', `${$route.path}/${dashboard.id}`)"
                          />
                        </div>
                        <div
                          v-if="Number.isInteger(dashboard.id)"
                          class="icon"
                        >
                          <i
                            :class="{ 'fas fa-heart': dashboard.isFavorite, 'far fa-heart': !dashboard.isFavorite }"
                            @click="togglePreference('favorite', dashboard.id, { isActive: !dashboard.isFavorite, id: dashboard.favoriteId })"
                          />
                        </div>
                      </template>
                      <template v-else>
                        <Tooltip
                          content="Share Dashboard"
                          placement="top-end"
                          class="icon"
                        >
                          <i
                            class="fas fa-share-alt"
                            @click="shareLink(dashboard.id)"
                          />
                        </Tooltip>
                        <Tooltip
                          :content="`Email ${dashboard.owners}`"
                          placement="top-end"
                          class="icon"
                        >
                          <i
                            class="far fa-envelope"
                            @click="$feedback(dashboard.owners, dashboard.title, 'dashboard', `${$route.path}/${dashboard.id}`)"
                          />
                        </Tooltip>
                        <Tooltip
                          v-if="Number.isInteger(dashboard.id)"
                          content="Favorite Dashboard"
                          placement="top-end"
                          class="icon"
                        >
                          <i
                            :class="{ 'fas fa-heart': dashboard.isFavorite, 'far fa-heart': !dashboard.isFavorite }"
                            @click="togglePreference('favorite', dashboard.id, { isActive: !dashboard.isFavorite, id: dashboard.favoriteId })"
                          />
                        </Tooltip>
                      </template>
                    </div>
                  </div>
                  <Spin
                    v-if="loading"
                    fix
                    size="large"
                  />
                </div>
              </div>
            </template>
          </Row>
        </div>
      </div>
      <Page
        v-show="filteredDashboards.length > pageSize"
        :current="currentPage + 1"
        :page-size="pageSize"
        :total="filteredDashboards.length"
        show-elevator
        @on-change="onPageChange"
      />
      <DocumentEmbedding
        :show-document="showDashboard"
        :document-title="dashboardTitle"
        :document-type="dashboardType"
        :owners="dashboardOwners"
        :url="urlBase"
        :url-preview="urlPreview"
        :editable="true"
        :copyable="true"
        :downloadable="true"
        :is-owner="isOwner"
        :dashboard="dashboardType === 'new-relic-group' ? newRelicDashboard : selectedDashboard"
        @closePreview="onClosePreview"
      />
      <BackTop />
    </template>
  </div>
</template>
<script>
import DocumentEmbedding from './helper-components/DocumentEmbedding'
import DashboardFilter from './helper-components/DashboardFilter'
import HintBox from './helper-components/HintBox'
import isMobile from '@/helpers/isMobile'
import axios from 'axios'
import _ from 'lodash'

export default {
  components: {
    DocumentEmbedding,
    HintBox,
    DashboardFilter
  },

  props: {
    userId: Number,
    contentTop: Number,
    extraParam: Object
  },

  data () {
    const dummyDashboards = [...Array(24).keys()].map(val => ({
      id: val,
      icon: 'far fa-circle',
      title: `Dashboard ${val}`,
      description: `This is Dashboard ${val}`,
      tags: [],
      owners: 'Some Body'
    }))

    return {
      loading: true,
      dashboards: dummyDashboards,
      filterText: this.$root.preferences.dashboards.filterText || '',
      tagsList: [],
      selectedTags: this.$root.preferences.dashboards.selectedTags || [],
      ownersList: [],
      selectedOwner: this.$root.preferences.dashboards.selectedOwner || '',
      typesList: [],
      selectedType: this.$root.preferences.dashboards.selectedType || '',
      isMobile: isMobile(),
      tagCondition: this.$root.preferences.dashboards.tagFilterClause || 'and',
      context: 'dashboards',
      isFilterByFavorites: this.$root.preferences.dashboards.isFilterByFavorites || false,
      isShared: false,
      errorMessage: '',
      showDashboard: false,
      dashboardTitle: '',
      dashboardType: '',
      dashboardOwners: '',
      isOwner: false,
      urlBase: '',
      urlPreview: '',
      reportStartTime: null,
      newRelicDashboard: {},
      selectedDashboard: {},
      currentPage: 0,
      pageSize: 24,
      viewType: this.$root.preferences.dashboards.viewType || 'grid',
      hasExternalFilters: !_.isEmpty(this.$root.preferences.dashboards.selectedType),
      showHintBox: false,
      hintsPreferences: {},
      hints: [
        {
          id: 1,
          title: 'Hint',
          type: 'info',
          description: `Do you want to filter by <b>Keywords, Tags, Owner</b> or <b>Favorites</b>? Just click on the filter icon <i class="fas fa-filter" />.`,
          hidden: false
        }
      ],
      isNewRelicsFetched: false,
      filterSettings: {
        searchText: this.$root.preferences.dashboards.filterText || '',
        selectedTags: this.$root.preferences.dashboards.selectedTags || [],
        selectedOwners: this.$root.preferences.dashboards.selectedOwners || [],
        selectedType: this.$root.preferences.dashboards.selectedType || '',
        onlyFavorites: this.$root.preferences.dashboards.isFilterByFavorites || false
      }
    }
  },

  computed: {
    dashboardsPerPage () {
      const startingIndex = this.currentPage * this.pageSize
      return this.filteredDashboards.slice(startingIndex, startingIndex + this.pageSize)
    },

    tagOptions () {
      return this.tagsList.map(tag => tag.name)
    },

    ownerOptions () {
      return this.ownersList.map(owner => owner.name)
    },

    typeOptions () {
      return this.typesList.map(type => type.label)
    },

    hasFilters () {
      const { searchText, selectedTags, selectedOwners, selectedType, onlyFavorites } = this.filterSettings

      return searchText || selectedTags.length || selectedOwners.length || selectedType || onlyFavorites
    },

    filteredDashboards () {
      if (!this.hasFilters || this.loading) {
        return this.dashboards
      }

      const { searchText, selectedTags, selectedOwners, selectedType, onlyFavorites } = this.filterSettings
      const searchTextMatch = searchText.toLowerCase().trim()
      const selectedTagSet = new Set(selectedTags.map(tag => tag.toLowerCase()))
      const selectedOwnerSet = new Set(selectedOwners.map(tag => tag.toLowerCase()))
      const selectedTypeLowerCase = selectedType.toLowerCase()

      return this.dashboards.filter(dashboard => (
        dashboard.title.toLowerCase().match(searchTextMatch) &&
        (!selectedTagSet.size || (dashboard.tags && dashboard.tags.some(tag => selectedTagSet.has(tag)))) &&
        (!selectedOwnerSet.size || (dashboard.owners && dashboard.owners.split(';').map(owner => owner.toLowerCase()).some(owner => selectedOwnerSet.has(owner)))) &&
        (!selectedTypeLowerCase || dashboard.type.toLowerCase() === selectedTypeLowerCase) &&
        (!onlyFavorites || (onlyFavorites && dashboard.isFavorite))
      ))
    }
  },

  watch: {
    userId () {
      this.populateDashboards()
    },

    $route (newRoute) {
      if (newRoute.path.includes('/dashboards')) {
        this.populateDashboards()
      }
    },

    filteredDashboards: {
      handler () {
        this.currentPage = 0
      },
      deep: true
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('dashboards')) {
      this.$set(this.$root.preferences, 'dashboards', {})
    }

    // Set the filter tag condition as "AND" by default.
    this.$set(this.$root.preferences.dashboards, 'tagFilterClause', 'and')

    sessionStorage.setItem('context', 'dashboards')
  },

  mounted () {
    window.addEventListener('resize', this.checkMobile)

    if (this.userId) {
      this.populateDashboards()
    }

    let previousHintValue = false
    if (this.$root.preferences.dashboards.hasOwnProperty('blockHint')) {
      previousHintValue = this.$root.preferences.dashboards.blockHint
      delete this.$root.preferences.dashboards.blockHint
    }

    this.hintsPreferences = this.$root.preferences.dashboards.hints
      ? JSON.parse(this.$root.preferences.dashboards.hints)
      : {}
    this.hints.forEach(hint => {
      if (!this.hintsPreferences[hint.id]) {
        const isHidden = hint.id === 1 && previousHintValue
        this.hintsPreferences[hint.id] = isHidden
      }
      this.$set(hint, 'hidden', this.hintsPreferences[hint.id])
    })
    this.$set(this.$root.preferences.dashboards, 'hints', JSON.stringify(this.hintsPreferences))
    this.showHintBox = this.hints.some(hint => !hint.hidden)
  },

  methods: {
    capitalizeTagName (tagName) {
      return _.startCase(_.lowerCase(tagName))
    },

    /**
     * Changes the current page to a new desired page.
     * @param {Number} pageNum
     */
    onPageChange (pageNum) {
      this.currentPage = pageNum - 1
    },

    /**
     * Validates if the device is a mobile or not.
     * @param {Event} e
     */
    checkMobile (e) {
      this.isMobile = isMobile(e)
    },

    /**
     * Populates the dashboards array to be used into the module.
     */
    async populateDashboards () {
      this.loading = true
      this.isNewRelicsFetched = false
      const dashboardHasId = !!this.$route.params.id
      const sharedDashboardId = dashboardHasId ? this.$route.params.id : null
      this.isShared = dashboardHasId
      const favoriteDashboards = {}
      const likeDashboards = {}

      if (isNaN(sharedDashboardId)) {
        const newRelicDashboards = await axios.get(process.env.VUE_APP_API_URI + '/new-relic')
        this.isNewRelicsFetched = true
        if (newRelicDashboards.data.length) {
          this.newRelicDashboard = newRelicDashboards.data.find(dashboard => {
            const nonAlphanumeric = new RegExp('[^a-zA-Z0-9 -]', 'gi')
            const id = `new-relic-${(dashboard.title.replace(nonAlphanumeric, '').trim().replace(/\s+/g, '-')).toLowerCase()}`
            return id === sharedDashboardId
          }) || {}
          this.dashboards = !_.isEmpty(this.newRelicDashboard) ? [this.newRelicDashboard] : []
          this.previewDashboard(this.newRelicDashboard)
        }
      } else if (sharedDashboardId) {
        const dashboard = await axios.get(`${process.env.VUE_APP_API_URI}/dashboards/${sharedDashboardId}`)

        this.dashboards = [...dashboard.data]
        if (this.dashboards.length) {
          const sharedDashboard = { ...this.dashboards[0] }
          this.isMobile
            ? this.openDashboard(sharedDashboard)
            : this.previewDashboard(sharedDashboard)
        }
      } else {
        const dashboards = await axios.get(process.env.VUE_APP_API_URI + '/dashboards?isActive=true')
        this.getFilterValues(dashboards.data)
        this.dashboards = dashboards.data || []
      }

      if (this.dashboards.length === 0) {
        if (sharedDashboardId) {
          this.errorMessage = `No Dashboard matches the given id`
        } else {
          this.errorMessage = `There isn't registered any dashboard`
        }
        this.loading = false
        return
      }

      const favoriteRequest = axios.get(`${process.env.VUE_APP_API_URI}/users/${this.userId}/favorite`, {
        params: {
          componentType: 'dashboard'
        }
      })

      const likeRequest = axios.get(`${process.env.VUE_APP_API_URI}/users/${this.userId}/like`, {
        params: {
          componentType: 'dashboard'
        }
      })

      Promise.all([favoriteRequest, likeRequest]).then(values => {
        values[0].data.forEach(item => {
          favoriteDashboards[item.component_id] = item.id
        })

        values[1].data.forEach(item => {
          likeDashboards[item.component_id] = item.id
        })

        this.dashboards.forEach(dashboard => {
          dashboard.isFavorite = !!favoriteDashboards[dashboard.id]
          dashboard.favoriteId = favoriteDashboards[dashboard.id] || null
          dashboard.isLike = !!likeDashboards[dashboard.id]
          dashboard.likeId = likeDashboards[dashboard.id] || null
        })

        if (!sharedDashboardId) {
          axios.get(process.env.VUE_APP_API_URI + '/new-relic').then(newRelicDashboards => {
            if (newRelicDashboards.data.length) {
              newRelicDashboards.data.map(dashboard => {
                const nonAlphanumeric = new RegExp('[^a-zA-Z0-9 -]', 'gi')
                const id = `new-relic-${(dashboard.title.replace(nonAlphanumeric, '').trim().replace(/\s+/g, '-')).toLowerCase()}`
                this.$set(dashboard, 'id', id)
                return dashboard
              })
            }
            this.isNewRelicsFetched = true
            this.getFilterValues(newRelicDashboards.data)
            this.dashboards = [...this.dashboards] // new-relic removed: ...(newRelicDashboards.data || [])
          }).catch(error => {
            this.isNewRelicsFetched = true
            console.error(error)
          })
        }

        this.loading = false
      })
    },

    /**
     * Get the values to be used into the filter component:
     * Only getting values from active dashboards
     *   - Tags
     *   - Owners
     *   - Dashboard Types
     */
    getFilterValues (dashboardsList = []) {
      // TODO: could change these objects into Set
      const dashboardsTagsSet = {}
      const dashboardsOwnersSet = {}
      const dashboardsTypesSet = {}

      dashboardsList.filter(dashboard => dashboard.active).forEach(dashboard => {
        const dashboardTags = dashboard.categories.toLowerCase().split(';') || []
        this.$set(dashboard, 'tags', dashboardTags)
        dashboardTags.forEach(tag => {
          tag = tag.toLowerCase()
          if (!dashboardsTagsSet[tag] && !this.tagsList.find(tagObj => tagObj.name === tag)) {
            dashboardsTagsSet[tag] = true
          }
        })

        const dashboardOwners = dashboard.owners.split(';') || []
        dashboardOwners.forEach(owner => {
          if (!dashboardsOwnersSet[owner] && !this.ownersList.find(ownerObj => ownerObj.name === owner)) {
            dashboardsOwnersSet[owner] = true
          }
        })

        const dashboardType = dashboard.type.replace(/-/gi, ' ').toLowerCase()
        if (!dashboardsTypesSet[dashboardType] && !this.typesList.find(type => type.value === dashboard.type)) {
          dashboardsTypesSet[dashboardType] = dashboard.type
        }
      })

      const tagsList = Object.keys(dashboardsTagsSet).sort().map(tag => ({
        name: tag,
        selected: this.selectedTags.includes(tag)
      }))
      this.tagsList = [...this.tagsList, ...tagsList]

      const ownersList = Object.keys(dashboardsOwnersSet).sort().map(owner => ({
        name: owner,
        selected: owner === this.selectedOwner
      }))
      this.ownersList = [...this.ownersList, ...ownersList]

      const typesList = Object.keys(dashboardsTypesSet).sort().map(type => ({
        label: type,
        value: dashboardsTypesSet[type],
        selected: type === this.selectedType
      }))
      this.typesList = [...this.typesList, ...typesList]
    },

    /**
     * Shows the selected dashboard with the following clauses:
     *   - If the dashboard is a Google Spradsheet or a Tableau, it will be displayed as a preview.
     *   - Otherwise, the dashboard will be opened in a new window in its original version.
     * @param {Object} dashboard
     */
    previewDashboard (dashboard) {
      if (!_.has(dashboard, 'type')) {
        return
      }

      this.isOwner = dashboard.owners.split(';').includes(this.$auth.getUser().email)
      this.selectedDashboard = dashboard
      this.dashboardTitle = dashboard.title
      this.dashboardType = dashboard.type.toLowerCase()
      this.dashboardOwners = dashboard.owners
      this.$log('report_open', dashboard.title, { type: this.dashboardType })

      if (['superset', 'google script'].includes(this.dashboardType)) {
        this.openDashboard(dashboard)
        return
      }

      if (this.dashboards.length > 1) {
        this.reportStartTime = new Date().getTime()
        this.$router.push(`/dashboards/${dashboard.id}`)
        return
      }

      if (this.dashboardType === 'new-relic-group') {
        this.reportStartTime = new Date().getTime()
        this.newRelicDashboard = dashboard
        this.urlBase = ''
        this.urlPreview = ''
        this.showDashboard = true
      } else {
        switch (this.dashboardType) {
          case 'new-relic':
            this.urlBase = dashboard.url
            this.urlPreview = this.urlBase
            this.showDashboard = true
            break
          case 'google':
            this.urlBase = dashboard.url.split('/edit')[0]
            this.urlPreview = `${this.urlBase}?rm=minimal`
            this.showDashboard = true
            break
          case 'tableau':
            this.urlBase = dashboard.url.replace('#/', '').split('?:iid')[0]
            this.urlPreview = `${this.urlBase}?iframeSizedToWindow=true&:embed=yes&:toolbar=top`
            this.showDashboard = true
            break
          case 'custom':
            this.showDashboard = false
            this.$router.push(dashboard.url)
            break
          default:
            this.urlBase = ''
            this.urlPreview = ''
        }
        this.newRelicDashboard = ''
      }
    },

    /**
     * Opens the dashboard's link
     * @param {Object} dashboard
     */
    openDashboard (dashboard) {
      if (dashboard.type.toLowerCase() === 'new-relic-group') {
        const nonAlphanumeric = new RegExp('[^a-zA-Z0-9 -]', 'gi')
        const id = `new-relic-${(dashboard.title.replace(nonAlphanumeric, '').trim().replace(/\s+/g, '-')).toLowerCase()}`
        dashboard.url = `/dashboards/${id}`
      }
      this.$log('report_source_open', dashboard.title)
      window.open(dashboard.url, '_blank')
      this.$router.replace({ name: 'Dashboards' })
    },

    /**
     * Copies the share link into the clipboard.
     * @param {String} item // Dashboard ID
     */
    shareLink (item) {
      const linkToShare = `${window.location.origin}/dashboards/${item}`
      this.$log('dashboard_share', item)
      this.$copyText(linkToShare).then(
        this.onCopySuccess.bind(this),
        this.onCopyError.bind(this)
      )
    },

    /**
     * Updates the user preference such as "LIKES" or "FAVORITES" on an specific dashboard
     * @param {String} preferenceType "like" / "favorite"
     * @param {Number} dashboardId
     * @param {Object} properties Contains the properties regarding the "like" / "favorite" component (isActive, id).
     */
    async togglePreference (preferenceType, dashboardId, properties) {
      const dashboard = this.dashboards.find(x => x.id === dashboardId)
      const actionURL = preferenceType.toLowerCase() === 'like' ? `/users/like` : `/users/favorite`
      const method = properties.isActive ? 'post' : 'delete'
      const dataParams = properties.isActive ? {
        userId: this.userId,
        componentType: 'dashboard',
        componentId: dashboardId
      } : {
        id: properties.id,
        componentType: 'dashboard'
      }

      await axios({
        method: method,
        url: process.env.VUE_APP_API_URI + actionURL,
        headers: {
          'Content-Type': 'application/json'
        },
        data: dataParams
      })
        .then(result => {
          if (result.data.hasOwnProperty('error')) {
            throw new Error(result.data.error.message)
          }

          let dashboardLogLabel = ''
          if (preferenceType === 'favorite') {
            this.$set(dashboard, 'isFavorite', properties.isActive)
            dashboardLogLabel = properties.isActive ? 'dashboard_favorite' : 'dashboard_unfavorite'

            if (result.data.length > 0) {
              this.$set(dashboard, 'favoriteId', result.data[0].id)
            }
          } else {
            this.$set(dashboard, 'isLike', properties.isActive)
            dashboardLogLabel = properties.isActive ? 'dashboard_like' : 'dashboard_unlike'

            if (result.data.length > 0) {
              this.$set(dashboard, 'likeId', result.data[0].id)
            }
          }
          this.$log(dashboardLogLabel, dashboard.title, { dashboardId: dashboardId })
        })

      if (preferenceType === 'like') {
        await axios.get(process.env.VUE_APP_API_URI + '/user-actions/total-likes', {
          params: {
            componentType: 'dashboard',
            componentId: dashboardId
          }
        })
          .then(result => {
            const totalLikes = result.data[0].total_likes
            this.$set(dashboard, 'total_likes', totalLikes)
          })
      }

      // Re-render the dashboards list to display the changes on preference.
      this.dashboards = [...this.dashboards]
    },

    /**
     * Closes dashboard preview info in order to not be displayed when another dashboard preview is required.
     * @param {Boolean} isPreviewOpen
     */
    onClosePreview (isPreviewOpen) {
      if (isPreviewOpen) {
        return
      }

      const reportEndTime = new Date().getTime()
      const reportDuration = reportEndTime - this.reportStartTime
      this.reportStartTime = null
      this.urlPreview = ''
      this.showDashboard = false
      this.newRelicDashboard = {}

      const hours = Math.floor(reportDuration / (1000 * 60 * 60))
      const minutes = Math.floor((reportDuration % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((reportDuration % (1000 * 60)) / 1000)
      const elapsedTime = ((hours > 0 ? `${hours} hrs` : '') + (minutes > 0 ? ` ${minutes} mins` : '') + (seconds > 0 ? ` ${seconds} secs` : '')).trim()

      this.$log('report_close', this.dashboardTitle, { elapsedTime })
      this.$router.push('/dashboards')
    },

    toggleTag (tagName) {
      const tagSet = new Set(this.filterSettings.selectedTags)

      if (tagSet.has(tagName)) {
        tagSet.delete(tagName)
      } else {
        tagSet.add(tagName)
      }

      this.onSelectedTagsChange(Array.from(tagSet))
    },

    /**
       * Displays a success message if the copy to clipboard was successful.
       */
    onCopySuccess () {
      this.$Message.info({
        content: 'Copied to clipboard!',
        duration: 2
      })
    },

    /**
       * Displays an error message if the copy to clipboard failed.
       */
    onCopyError () {
      this.$Message.error({
        content: 'Error while copying to clipboard!',
        duration: 2
      })
    },

    /**
     * Returns the icon type: ivew icon / Font-Aswesome icon
     * @param {String} icon
     * @return {String}
     */
    iconType (icon) {
      return icon.split(' ').length < 2 ? 'iview' : 'fa'
    },

    /**
     * Toggles the result view: Grid / List.
     * @param {String} viewType
     */
    changeView (viewType) {
      this.viewType = viewType
      this.$set(this.$root.preferences.dashboards, 'viewType', viewType)
    },

    /**
     * Updates the preference about to block hints.
     * @param {Number} hintId
     * @param {Boolean} isHidden
     */
    updateHintPreference (hintId, isHidden) {
      this.hintsPreferences[hintId] = isHidden
      const updatedHint = this.hints.find(hint => hint.id === hintId)
      this.$set(updatedHint, 'hidden', isHidden)
      this.$set(this.$root.preferences.dashboards, 'hints', JSON.stringify(this.hintsPreferences))
    },

    onSearchTextChange (searchText) {
      this.$set(this.filterSettings, 'searchText', searchText)
      this.$set(this.$root.preferences.dashboards, 'filterText', searchText)
    },

    onSelectedTagsChange (selectedTags) {
      this.$set(this.filterSettings, 'selectedTags', selectedTags)
      this.$set(this.$root.preferences.dashboards, 'selectedTags', selectedTags)
    },

    onSelectedOwnersChange (selectedOwners) {
      this.$set(this.filterSettings, 'selectedOwners', selectedOwners)
      this.$set(this.$root.preferences.dashboards, 'selectedOwners', selectedOwners)
    },

    onSelectedTypeChange (selectedType) {
      this.$set(this.filterSettings, 'selectedType', selectedType)
      this.$set(this.$root.preferences.dashboards, 'selectedType', selectedType)
    },

    onOnlyFavoritesChange (onlyFavorites) {
      this.$set(this.filterSettings, 'onlyFavorites', onlyFavorites)
      this.$set(this.$root.preferences.dashboards, 'isFilterByFavorites', onlyFavorites)
    },

    clearFilters () {
      this.onSelectedTagsChange([])
      this.onSelectedOwnersChange([])
      this.onSelectedTypeChange('')
      this.onOnlyFavoritesChange(false)
    }
  }
}
</script>

<style scoped>
  .hint-text {
    font-size: 14px;
    line-height: unset;
  }

  .results {
    margin-top: 10px;
    width: 100%;
  }

  .dashboards-row {
    justify-content: center;
  }

  .no-data-msg {
    text-align: center;
    font-size: 16px;
    margin-top: 15%;
    margin-bottom: 15%;
  }

  .col-left {
    width: calc(100% - 164px);
  }

  .col-right {
    width: 154px;
    margin-left: 10px;
  }

  .card-block {
    width: 100%;
  }

  .list-block {
    padding: 8px;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--color-light-gray);
    border: 1px solid var(--color-gray-border);
  }

  .list-block:not(:first-child) {
    border-top: none;
  }

  .list-block:nth-child(odd) {
    background-color: var(--color-white);
  }

  .list-block:last-child {
    margin-bottom: 16px;
  }

  .list-block .list-row {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
  }

  .list-block .list-row .title-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    cursor: pointer;
  }

  .list-block .list-row .title-section .title {
    margin-right: 8px;
    font-size: 14px;
    font-weight: bold;
    width: calc(100% - 8px);
  }

  .list-block .list-row .title-section .tags-section {
    margin-right: 8px;
    display: flex;
    flex-direction: row;
    width: calc(100% - 8px);
  }

  .list-block .list-row .icons-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
  }

  .list-block .list-row .icons-section .left-section {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .list-block .list-row .icons-section .right-section {
    width: 96px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .list-block .list-row .icons-section .icon {
    font-size: 16px;
    margin: 0;
    cursor: pointer;
    width: 32px;
    text-align: center;
  }

  .list-block .list-row .icons-section .icon i {
    width: 16px;
    text-align: center;
  }

  .list-block .list-row .icons-section .like-text {
    display: flex;
    flex-direction: row;
  }

  .list-block .list-row .icons-section .like-text span {
    display: inline-flex;
    margin-left: 4px;
    margin-right: 8px;
  }

  .search-box,
  .search-box >>> .ivu-input {
    height: 40px;
  }

  .dashboard-link-card {
    margin-bottom: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    cursor: pointer;
    border-radius: 10px 10px 0 0;
  }

  .dashboard-link-card.ivu-card {
    transition: none;
  }

  .dashboard-card-body {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: baseline;
    text-align: center;
    justify-content: flex-start;
  }

  .dashboard-card-body-title {
    top: 0;
    width: 100%;
    font-size: .9rem;
    margin-bottom: 8px;
  }

  .dashboard-card-footer {
    position: relative;
    margin-top: 16px;
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .dashboard-link-card.ivu-card >>> .ivu-card-body {
    width: 100%;
    height: 100%;
    position: relative;
    padding: 32px 16px 8px;
  }

  .dashboard-card-logo {
    position: absolute;
    top: -24px;
    left: 0;
    width: 100%;
    display: flex;
    margin: 0;
    justify-content: center;
  }

  .dashboard-card-logo .dashboard-logo {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: var(--color-white);
    border: 1px solid var(--color-border);
    box-shadow: 0 1px 6px var(--color-box-shadow);
  }

  .dashboard-card-logo .dashboard-logo .logo-icon {
    font-size: 42px;
    color: var(--color-green-button);
  }

  .dashboard-card-logo .dashboard-logo .logo-icon.fa {
    font-size: 32px;
  }

  .description {
    position: relative;
    display: block;
    display: -moz-box;
    display: -webkit-box;
    width: 100%;
    min-height: calc(12px * 1.3 * 3);
    margin: 0 auto;
    font-size: 12px;
    line-height: 1.3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: justify;
    box-sizing: border-box;
  }

  .actions-section {
    position: relative;
    left: 0;
    bottom: 0;
    margin: 0 0 32px;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    height: 48px;
    border-radius: 0 0 10px 10px;
    border: 1px solid var(--color-card-border);
    border-top: none;
    background-color: var(--color-white);
  }

  .actions-section .footer-section {
    padding: 0 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .actions-section .footer-section.left {
    width: 40%;
    justify-content: flex-start;
  }

  .actions-section .footer-section.left .footer-like-text {
    padding-left: 8px;
  }

  .actions-section .footer-section.right {
    width: 60%;
    justify-content: flex-end;
  }

  .actions-section .footer-icon {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-green-lime);
  }

  .actions-section .footer-icon i {
    font-size: 20px;
    cursor: pointer;
  }

  .show-tags-row {
    display: block;
    margin-bottom: 20px;
  }

  .tag {
    max-width: unset;
    border-color: var(--color-card-border);
    background-color: transparent;
  }

  .tag >>> .ivu-tag-text {
    color: var(--color-black);
  }

  .tag.share {
    border: unset;
    background: unset;
    margin: 0;
    padding: 0;
  }

  .tag.share .logo-icon.ivu-icon {
    font-size: 16px;
    color: var(--color-green-lime);
  }

  .selected-tags.bottom {
    width: 100%;
    margin-left: 0;
  }

  .filter .dropdown .elements .row {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .filter .dropdown .elements .row .element-row,
  .filter .dropdown .elements .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .filter .dropdown .elements .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .filter .dropdown .elements .row .element-row p {
    margin: 0;
  }

  .right {
    display: flex;
    justify-content: flex-end;
  }

  .filter .dropdown .elements .row .select-input {
    width: 100%;
  }

  .filter .dropdown .elements .row .select-input.left >>> .ivu-select-selection {
    width: 100%;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.right >>> .ivu-select-selection {
    width: 100%;
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.big-list >>> .ivu-select-dropdown {
    transform-origin: center top !important;
    top: unset !important;
    max-height: 400px;
    overflow-y: scroll;
  }

  .filter .dropdown .elements .row .select-input >>> .ivu-select-not-found {
    color: var(--color-red-alert);
    font-size: 12px;
    list-style: none;
    text-align: left;
    padding: 7px 16px;
  }

  .capitalize {
    text-transform: capitalize;
  }

  @media screen and (min-width: 375px) {
    .col-left {
      width: calc(100% - 194px);
    }
    .col-right {
      width: 184px;
    }
    .dashboard-card-body-title {
      font-size: 1rem;
    }
  }

  @media screen and (min-width: 425px) {
    .no-data-msg {
      font-size: 24px;
    }

    .card-block {
      width: 425px;
    }

    .dashboard-link-card {
      height: 160px;
    }

    .description {
      height: calc(12px * 1.4 * 3);
      font-size: 12px;
      line-height: 1.4;
      -webkit-line-clamp: 3;
    }
  }

  @media screen and (min-width: 728px) and (max-width: 999px) {
    .no-data-msg {
      font-size: 32px;
    }

    .card-block {
      width: 50%;
      padding-right: 8px;
    }

    .card-block .dashboard-link-card,
    .actions-section {
      width: 352px;
      max-width: 352px;
    }

    .list-block .list-row {
      flex-direction: row;
    }

    .list-block .list-row .icons-section {
      width: 184px;
    }

    .list-block .list-row .icons-section .left-section {
      width: 88px;
    }

    .show-tags-row {
      display: none;
    }
  }

  @media screen and (max-width: 999px) {
    .dashboard-link-card {
      max-width: 480px;
      margin-top: 32px;
      height: auto;
    }

    .dashboard-card-body {
      height: 100%;
      margin-top: 8px;
    }

    .actions-section .footer-section.right .footer-icon {
      margin-left: 20px;
    }
  }

  @media screen and (min-width: 1000px) {
    .results {
      margin-top: 24px;
    }

    .results.list {
      margin-top: 8px;
    }

    .dashboards-row {
      justify-content: flex-start;
    }

    .list-block .list-row {
      flex-direction: row;
    }

    .list-block .list-row .title-section .tag >>> .ivu-tag-text {
      font-size: 11px;
    }

    .list-block .list-row .icons-section {
      width: 184px;
    }

    .list-block .list-row .icons-section .left-section {
      width: 88px;
    }

    .no-data-msg {
      font-size: 32px;
    }

    .col-left {
      width: calc(100% - 240px);
    }

    .col-right {
      width: 232px;
      margin-left: 8px;
    }

    .card-block {
      width: max-content;
      padding: 0;
      box-sizing: border-box;
    }

    .dashboard-card-body-title {
      font-size: 14px;
    }

    .tag.share .logo-icon.ivu-icon {
      font-size: 24px;
    }

    .description {
      height: calc(14px * 1.3 * 3);
      font-size: 14px;
      line-height: 1.3;
      -webkit-line-clamp: 3;
    }

    .dashboard-link-card {
      top: 24px;
      height: 204px;
    }

    .dashboard-link-card.ivu-card {
      transition: none;
    }

    .dashboard-link-card.ivu-card >>> .ivu-card-body {
      height: auto;
      padding: 48px 24px 8px;
      top: -24px;
    }

    .dashboard-link-card.ivu-card >>> .ivu-card-body .dashboard-card-body {
      height: 130px;
    }

    .dashboard-card-logo {
      top: -32px;
    }

    .dashboard-card-logo .dashboard-logo {
      width: 72px;
      height: 72px;
    }

    .dashboard-card-logo .dashboard-logo .logo-icon {
      font-size: 62px;
    }

    .dashboard-card-logo .dashboard-logo .logo-icon.fa {
      font-size: 42px;
    }

    .card-block.ivu-col:hover .dashboard-link-card,
    .card-block.ivu-col:hover .actions-section {
      box-shadow: 0 1px 6px var(--color-box-shadow);
      border-color: var(--color-border);
    }

    .dashboard-link-card,
    .actions-section {
      width: 384px;
      max-width: 384px;
    }

    .actions-section {
      border-top: 1px solid var(--color-card-border);
    }

    .actions-section .footer-section.left .footer-like-text {
      padding: 0;
    }

    .actions-section .footer-icon {
      margin: 0 8px;
    }
  }

  @media screen and (min-width: 1192px) {
    .card-block:nth-child(3n+2) {
      padding-left: calc((100% - (384px * 3)) / 2);
      padding-right: calc((100% - (384px * 3)) / 2);
    }
  }

  @media screen and (min-width: 1600px) {
    .card-block:nth-child(n+1) {
      padding: 0
    }
    .card-block:nth-child(4n+2),
    .card-block:nth-child(4n+3),
    .card-block:nth-child(4n+4) {
      padding-left: calc((100% - (384px * 4)) / 3);
    }
  }

  @media screen and (min-width: 2560px) {
    .card-block:nth-child(n+1) {
      padding: 0
    }
    .card-block:nth-child(6n+2),
    .card-block:nth-child(6n+3),
    .card-block:nth-child(6n+4),
    .card-block:nth-child(6n+5),
    .card-block:nth-child(6n+6) {
      padding-left: calc((100% - (384px * 6)) / 5);
    }
  }

  .loading-veil {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 8;
    width: 100%;
    height: 100%;
    background-color: rgba(255,255,255,.9);
  }

  .dashboard-logo .loading-veil {
    border-radius: 50%;
  }

  .dashboard-filter {
    padding: 16px 0;
    position: sticky;
    position: -webkit-sticky;
    background-color: #f5f7f9;
    z-index: 10;
  }
</style>
